import React from 'react'
import { Store } from 'lucide-react'
import { cn } from '@/utils/cn'

const LOGO_BASE = '/marketplace-integrations'

const ICON_KEY_TO_SRC: Record<string, string> = {
  whatsapp: `${LOGO_BASE}/whatsapp.png`,
  facebook: `${LOGO_BASE}/meta.png`,
  mercadolivre: `${LOGO_BASE}/mercado-livre.png`,
  olx: `${LOGO_BASE}/olx.png`,
}

export function marketplaceLogoSrcFromIconKey(iconKey: string): string | undefined {
  return ICON_KEY_TO_SRC[iconKey]
}

export function marketplaceLogoSrcFromPlatform(platform: string): string | undefined {
  const normalized = platform.toUpperCase().replace(/-/g, '_')
  if (normalized === 'META') {
    return ICON_KEY_TO_SRC.facebook
  }
  if (normalized === 'MERCADO_LIVRE' || normalized === 'MERCADOLIVRE') {
    return ICON_KEY_TO_SRC.mercadolivre
  }
  if (normalized === 'OLX') {
    return ICON_KEY_TO_SRC.olx
  }
  if (normalized === 'WHATSAPP') {
    return ICON_KEY_TO_SRC.whatsapp
  }
  return undefined
}

interface MarketplaceIntegrationLogoProps {
  iconKey?: string
  platform?: string
  alt: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const SIZE_MAP: Record<NonNullable<MarketplaceIntegrationLogoProps['size']>, string> = {
  sm: 'h-8 w-8 min-h-8 min-w-8',
  md: 'h-14 w-14 min-h-14 min-w-14',
  lg: 'h-24 w-24 min-h-24 min-w-24 sm:h-28 sm:w-28 sm:min-h-28 sm:min-w-28',
  xl: 'h-auto w-auto max-h-[min(14rem,72vw)] max-w-[min(14rem,90vw)] sm:max-h-[15rem] sm:max-w-[15rem] min-h-0 min-w-0',
}

export function MarketplaceIntegrationLogo({
  iconKey,
  platform,
  alt,
  size = 'md',
  className,
}: MarketplaceIntegrationLogoProps): React.ReactElement {
  const resolvedSize: NonNullable<MarketplaceIntegrationLogoProps['size']> = size ?? 'md'
  const src =
    (iconKey !== undefined ? marketplaceLogoSrcFromIconKey(iconKey) : undefined) ??
    (platform !== undefined ? marketplaceLogoSrcFromPlatform(platform) : undefined)

  const dimensionClass = SIZE_MAP[resolvedSize]

  if (src === undefined) {
    return (
      <Store
        className={cn('text-muted-foreground shrink-0 object-contain', dimensionClass, className)}
        aria-hidden
      />
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn('object-contain shrink-0 rounded-md', dimensionClass, className)}
      loading="lazy"
      decoding="async"
    />
  )
}
